import React, { createContext, useContext } from 'react';
import { useAccessControl } from '@/hooks/useAccessControl';
import type { Feature } from '@/config/plans';
import type { UserProfile } from '@/types/user';

interface AccessControlContextType {
  userProfile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  checkFeatureAccess: (feature: Feature) => {
    hasAccess: boolean;
    reason?: string;
    minimumPlan?: import('@/config/plans').SubscriptionPlan;
    upgradeRequired?: boolean;
    subscriptionInactive?: boolean;
  };
  checkMultipleFeatures: (features: Feature[]) => Record<Feature, {
    hasAccess: boolean;
    reason?: string;
    minimumPlan?: import('@/config/plans').SubscriptionPlan;
    upgradeRequired?: boolean;
    subscriptionInactive?: boolean;
  }>;
  getCurrentPlan: () => {
    plan: import('@/config/plans').SubscriptionPlan;
    status: import('@/config/plans').SubscriptionStatus;
    label: string;
    isActive: boolean;
    startedAt?: string;
    endsAt?: string;
    trialEndsAt?: string;
  } | null;
  refreshProfile: () => Promise<void>;
}

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined);

/**
 * Provider de controle de acesso
 * Deve ser usado no topo da árvore de componentes (após ThemeProvider)
 */
export const AccessControlProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const accessControl = useAccessControl();

  return (
    <AccessControlContext.Provider value={accessControl}>
      {children}
    </AccessControlContext.Provider>
  );
};

/**
 * Hook para acessar o contexto de controle de acesso
 * Use este hook em vez de useAccessControl diretamente quando dentro do Provider
 */
export const useAccessControlContext = () => {
  const context = useContext(AccessControlContext);
  if (context === undefined) {
    throw new Error('useAccessControlContext must be used within AccessControlProvider');
  }
  return context;
};

/**
 * Hook opcional que pode funcionar com ou sem o provider
 * Útil para componentes que podem ser usados em diferentes contextos
 */
export const useAccessControlOptional = () => {
  const context = useContext(AccessControlContext);
  const standalone = useAccessControl();
  
  return context || standalone;
};
